import React, { useState } from 'react'
import { faker } from '@faker-js/faker'
import Prism from 'prismjs'

import 'prismjs/themes/prism.css'

import { useAuth } from '../contexts/AuthContext'
import { BusinessIndustry } from './dataImportFlow/importDummyDataHelpers'

interface FormFieldProps {
  label: string
  value: string
  onChangeHandler: (value: string) => void
  options?: string[]
  required?: boolean
}

const FormField = ({
  label,
  value,
  onChangeHandler,
  options,
  required,
}: FormFieldProps) => {
  const inputElement = options ? (
    <select
      value={value}
      onChange={e => onChangeHandler(e.target.value)}
      required={required ?? true}
    >
      {options.map(option => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  ) : (
    <input
      type='text'
      value={value}
      onChange={e => onChangeHandler(e.target.value)}
      required={required ?? true}
    />
  )

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        marginBottom: '10px',
      }}
    >
      <label>
        {label}
        {(required ?? true) && (
          <span style={{ color: 'red', marginLeft: '2px' }}>*</span>
        )}
      </label>
      {inputElement}
    </div>
  )
}

const US_STATES = [
  'Alabama',
  'Alaska',
  'Arizona',
  'Arkansas',
  'California',
  'Colorado',
  'Connecticut',
  'Delaware',
  'District of Columbia',
  'Florida',
  'Georgia',
  'Hawaii',
  'Idaho',
  'Illinois',
  'Indiana',
  'Iowa',
  'Kansas',
  'Kentucky',
  'Louisiana',
  'Maine',
  'Maryland',
  'Massachusetts',
  'Michigan',
  'Minnesota',
  'Mississippi',
  'Missouri',
  'Montana',
  'Nebraska',
  'Nevada',
  'New Hampshire',
  'New Jersey',
  'New Mexico',
  'New York',
  'North Carolina',
  'North Dakota',
  'Ohio',
  'Oklahoma',
  'Oregon',
  'Pennsylvania',
  'Rhode Island',
  'South Carolina',
  'South Dakota',
  'Tennessee',
  'Texas',
  'Utah',
  'Vermont',
  'Virginia',
  'Washington',
  'West Virginia',
  'Wisconsin',
  'Wyoming',
  'Puerto Rico',
]

const BUSINESS_INDUSTRIES = Object.values(BusinessIndustry).map(value =>
  value.toString(),
)

const COUNTRY_CODES = [
  'AF', 'AL', 'DZ', 'AS', 'AD', 'AO', 'AI', 'AQ', 'AG', 'AR', 'AM', 'AW',
  'AU', 'AT', 'AZ', 'BS', 'BH', 'BD', 'BB', 'BY', 'BE', 'BZ', 'BJ', 'BM',
  'BT', 'BO', 'BA', 'BW', 'BV', 'BR', 'IO', 'BN', 'BG', 'BF', 'BI', 'KH',
  'CM', 'CA', 'CV', 'KY', 'CF', 'TD', 'CL', 'CN', 'CX', 'CC', 'CO', 'KM',
  'CG', 'CD', 'CK', 'CR', 'CI', 'HR', 'CU', 'CY', 'CZ', 'DK', 'DJ', 'DM',
  'DO', 'EC', 'EG', 'SV', 'GQ', 'ER', 'EE', 'ET', 'FK', 'FO', 'FJ', 'FI',
  'FR', 'GF', 'PF', 'TF', 'GA', 'GM', 'GE', 'DE', 'GH', 'GI', 'GR', 'GL',
  'GD', 'GP', 'GU', 'GT', 'GN', 'GW', 'GY', 'HT', 'HM', 'VA', 'HN', 'HK',
  'HU', 'IS', 'IN', 'ID', 'IR', 'IQ', 'IE', 'IL', 'IT', 'JM', 'JP', 'JO',
  'KZ', 'KE', 'KI', 'KP', 'KR', 'KW', 'KG', 'LA', 'LV', 'LB', 'LS', 'LR',
  'LY', 'LI', 'LT', 'LU', 'MO', 'MK', 'MG', 'MW', 'MY', 'MV', 'ML', 'MT',
  'ME', 'MH', 'MQ', 'MR', 'MU', 'YT', 'MX', 'FM', 'MD', 'MC', 'MN', 'MS',
  'MA', 'MZ', 'MM', 'NA', 'NR', 'NP', 'NL', 'NC', 'NZ', 'NI', 'NE', 'NG',
  'NU', 'NF', 'MP', 'NO', 'OM', 'PK', 'PW', 'PS', 'PA', 'PG', 'PY', 'PE',
  'PH', 'PN', 'PL', 'PT', 'PR', 'QA', 'RE', 'RO', 'RU', 'RS', 'RW', 'SH',
  'KN', 'LC', 'PM', 'VC', 'WS', 'SM', 'ST', 'SA', 'SN', 'SC', 'SL', 'SG',
  'SK', 'SI', 'SB', 'SO', 'ZA', 'GS', 'ES', 'LK', 'SD', 'SR', 'SJ', 'SZ',
  'SE', 'CH', 'SY', 'TW', 'TJ', 'TZ', 'TH', 'TL', 'TG', 'TK', 'TO', 'TT',
  'TN', 'TR', 'TM', 'TC', 'TV', 'UG', 'UA', 'AE', 'GB', 'US', 'UM', 'UY',
  'UZ', 'VU', 'VE', 'VN', 'VG', 'VI', 'WF', 'EH', 'YE', 'ZM', 'ZW',
]

interface CreateBusinessProps {
  demoName: string
  bearerToken: string
  layerApiUrl: string
  onBusinessCreated: (businessId: string) => void
}

export const CreateBusiness = ({
  demoName,
  bearerToken,
  layerApiUrl,
  onBusinessCreated,
}: CreateBusinessProps) => {
  const [externalId, setExternalId] = useState('')
  const [legalName, setLegalName] = useState('')
  const [tin, setTin] = useState<string>('')
  const [country, setCountry] = useState('US')
  const [usState, setUsState] = useState('California')
  const [entityType, setEntityType] = useState('LLC')
  const [industry, setIndustry] = useState('GENERIC')
  const [phoneNumber, setPhoneNumber] = useState('')
  const [activationAt, setActivationAt] = useState('2023-04-15T14:13:07Z')
  const [plaidProcessorTokens, setPlaidProcessorTokens] = useState<string[]>([])
  const { authFetch } = useAuth()

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    // Prepare the data for the POST request to create a business
    const businessData = {
      external_id: externalId || undefined,
      legal_name: legalName || undefined,
      tin: tin || undefined,
      country,
      us_state: country === 'US' ? usState : undefined,
      entity_type: entityType,
      industry: industry,
      phone_number: phoneNumber || undefined,
      activation_at: activationAt || undefined,
      plaid_processor_tokens: plaidProcessorTokens.map(id => ({
        processor_token: id.trim(),
      })),
    }

    try {
      const createResponse = await fetch(`${layerApiUrl}/v1/businesses`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${bearerToken}`,
        },
        body: JSON.stringify(businessData),
      })

      const createResponseJson = await createResponse.json()
      if (!createResponse.ok) {
        throw new Error(createResponseJson?.errors?.[0]?.description)
      }
      if (!createResponseJson.data || !createResponseJson.data.id) {
        throw new Error('No business ID returned from creation API')
      }

      const businessId: string = createResponseJson.data.id

      const updateResponse = await authFetch(`/api/demos/${demoName}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessId: businessId,
        }),
      })

      if (!updateResponse.ok) {
        throw new Error('Failed to update demo')
      }

      onBusinessCreated(businessId)
      alert('Business created successfully')
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error in creating business or updating demo:', error)
      alert(`Error creating business: ${error}`)
    }
  }

  const generateCurlCommand = (): string => {
    const data = {
      external_id: externalId || undefined,
      legal_name: legalName || undefined,
      tin: tin || undefined,
      country,
      us_state: country === 'US' ? usState : undefined,
      entity_type: entityType,
      industry: industry,
      phone_number: phoneNumber || undefined,
      activation_at: activationAt || undefined,
      plaid_processor_tokens: plaidProcessorTokens.map(id => ({
        processor_token: id.trim(),
      })),
    }
    const formattedData = JSON.stringify(data, null, 2)

    return `curl -X POST ${layerApiUrl}/v1/businesses \\
    -H "Authorization: Bearer <platform_access_token>" \\
    -H "Content-Type: application/json" \\
    -d '${formattedData.replace(/'/g, '\'\\\'\'')}'` // eslint-disable-line quotes
  }

  Prism.highlightAll()
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
      }}
    >
      <h1>Create Business</h1>
      <form
        onSubmit={handleSubmit}
        style={{
          width: 400,
          margin: '10px',
          padding: '20px',
          border: '2px solid black',
          borderRadius: '5px',
        }}
      >
        <FormField
          label={'External ID:'}
          value={externalId}
          onChangeHandler={setExternalId}
          required={false}
        />
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
          <div style={{ flex: 1 }}>
            <FormField
              label={'Legal Name:'}
              value={legalName}
              onChangeHandler={setLegalName}
            />
          </div>
          <button
            type='button'
            onClick={() => {
              const variant = Math.floor(Math.random() * 4)
              const name = [
                `${faker.person.lastName()} Inc.`,
                `${faker.commerce.product()} Inc.`,
                `${faker.person.lastName()} & ${faker.person.lastName()} Inc.`,
                `${faker.commerce.product()} & ${faker.commerce.product()} Inc.`,
              ][variant]
              setLegalName(name)
            }}
            style={{ marginLeft: '8px', marginBottom: '10px' }}
          >
            Generate
          </button>
        </div>
        <FormField
          label={'TIN:'}
          value={tin ?? ''}
          onChangeHandler={setTin}
          required={false}
        />
        <FormField
          label={'Country:'}
          value={country}
          onChangeHandler={setCountry}
          options={COUNTRY_CODES}
        />
        <FormField
          label={'US State:'}
          value={usState}
          onChangeHandler={setUsState}
          options={US_STATES}
        />
        <FormField
          label={'Entity Type:'}
          value={entityType}
          onChangeHandler={setEntityType}
          options={['SOLE_PROP', 'C_CORP', 'LLC', 'S_CORP', 'PARTNERSHIP']}
        />
        <FormField
          label={'Industry:'}
          value={industry}
          onChangeHandler={setIndustry}
          options={BUSINESS_INDUSTRIES}
        />
        <FormField
          label={'Phone Number:'}
          value={phoneNumber}
          onChangeHandler={setPhoneNumber}
          required={false}
        />
        <FormField
          label={'Activation Date:'}
          value={activationAt}
          onChangeHandler={setActivationAt}
          required={false}
        />
        <FormField
          label={'Plaid Processor Tokens:'}
          value={plaidProcessorTokens.join(',')}
          onChangeHandler={v => setPlaidProcessorTokens(v.split(','))}
          required={false}
        />
        <div style={{ textAlign: 'right' }}>
          <button type='submit'>Create Business</button>
        </div>
      </form>
      <br />
      <pre style={{ fontSize: 12, backgroundColor: '#f5f2f0' }}>
        <code className='language-bash'>{generateCurlCommand()}</code>
      </pre>
    </div>
  )
}
